import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { ShieldAlert, TrendingUp, TrendingDown, DollarSign, Target, PieChart, Plus, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const GBE_NAVY = "#00334C";
const GBE_YELLOW = "#fcea00";

function KPI({ label, value, sub, icon: Icon, subColour }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="mt-1 font-display text-2xl text-slate-900">{value}</div>
      {sub && <div className={`text-xs mt-0.5 ${subColour || "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

const MONTH_LABEL = (yyyy_mm) => {
  if (!yyyy_mm) return "";
  const [y, m] = yyyy_mm.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const SOURCE_TONES = {
  myob:   { label: "MYOB",   cls: "bg-purple-100 text-purple-800 border-purple-200" },
  xero:   { label: "Xero",   cls: "bg-sky-100 text-sky-800 border-sky-200" },
  seed:   { label: "Seed",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  manual: { label: "Manual", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

function SourcePill({ source }) {
  const s = SOURCE_TONES[source] || SOURCE_TONES.seed;
  return (
    <span data-testid={`source-pill-${source || "seed"}`}
          className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function FinanceLegacyPage() {
  const { can, loaded } = usePermissions();
  const { setContext } = useAIContext();
  const [fy, setFy] = useState("FY26");
  const [buId, setBuId] = useState("all");
  const [bus, setBus] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm, setEntryForm] = useState({});
  const [entrySaving, setEntrySaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [insights, setInsights] = useState(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const [cutover, setCutover] = useState(null);
  const [cutoverSaving, setCutoverSaving] = useState(false);

  useEffect(() => {
    api.get("/org/business-units").then((r) => setBus(r.data || [])).catch(() => {});
    api.get("/finance/cutover").then((r) => setCutover(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loaded || !can("finance", "view")) return;
    setBusy(true);
    const params = new URLSearchParams({ fy });
    if (buId && buId !== "all") params.set("business_unit_id", buId);
    api.get(`/finance/pnl/summary?${params.toString()}`)
      .then((r) => {
        setData(r.data);
        setContext({
          module: "finance",
          summary: `Finance P&L for ${fy}${buId !== "all" ? " (BU-filtered)" : ""}`,
          kpis: r.data.kpis,
          trend: r.data.trend,
          sales_to_budget_by_bu: r.data.sales_to_budget_by_bu,
        });
      })
      .catch(() => toast.error("Failed to load P&L"))
      .finally(() => setBusy(false));

    // Fire off proactive AI insights (cached server-side per FY/BU/day)
    setInsights(null);
    setInsightsBusy(true);
    api.get(`/finance/pnl/insights?${params.toString()}`)
      .then((r) => setInsights(r.data.insights || []))
      .catch(() => setInsights([]))
      .finally(() => setInsightsBusy(false));

    return () => setContext(null);
    /* eslint-disable-next-line */
  }, [fy, buId, loaded, reloadTick]);

  const openEntry = () => {
    setEntryForm({
      business_unit_id: bus[0]?.id || "",
      period_month: new Date().toISOString().slice(0, 7),
      budget_sales: 0, sales: 0, opening_stock: 0, materials: 0,
      direct_wages: 0, subcontractors: 0, labour_hire: 0, closing_stock: 0,
    });
    setEntryOpen(true);
  };

  const saveEntry = async () => {
    setEntrySaving(true);
    try {
      const payload = { ...entryForm };
      for (const k of ["budget_sales", "sales", "opening_stock", "materials", "direct_wages", "subcontractors", "labour_hire", "closing_stock"]) {
        payload[k] = parseFloat(payload[k] || 0);
      }
      await api.post("/finance/pnl/entry", payload);
      toast.success("P&L entry saved");
      setEntryOpen(false);
      setReloadTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setEntrySaving(false);
    }
  };

  const trend = useMemo(
    () => (data?.trend || []).map((t) => ({ ...t, month_label: MONTH_LABEL(t.period) })),
    [data]
  );

  if (!loaded) return <AppLayout title="Financial"><div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" /></AppLayout>;

  if (!can("finance", "view")) {
    return (
      <AppLayout title="Financial · Cutover Legacy" subtitle="MYOB → Xero cutover management, invoices, bills">
        <div className="max-w-md mx-auto mt-16 text-center">
          <div className="w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-rose-700" />
          </div>
          <h2 className="font-display text-xl font-semibold text-slate-900">Access restricted</h2>
          <p className="text-slate-600 mt-2 text-sm">Your role does not have access to Finance.</p>
        </div>
      </AppLayout>
    );
  }

  const k = data?.kpis;
  const gmDelta = k?.gross_margin_pct_ly != null ? k.gross_margin_pct - k.gross_margin_pct_ly : null;
  const canEdit = can("finance", "edit");

  return (
    <AppLayout
      title="Financial · P&L Summary"
      subtitle="Sales · Budget · Gross Margin · Cost of Goods (MYOB source)"
      actions={
        <div className="flex items-center gap-2">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger data-testid={TID.financeFYSelect} className="w-28 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="FY25">FY25</SelectItem>
              <SelectItem value="FY26">FY26</SelectItem>
            </SelectContent>
          </Select>
          <Select value={buId} onValueChange={setBuId}>
            <SelectTrigger data-testid={TID.financeBUSelect} className="w-56 h-9 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Business Units</SelectItem>
              {bus.filter((b) => b.active !== false).map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.code ? `${b.code} · ${b.name}` : b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button data-testid={TID.financeBudgetInput} onClick={openEntry} className="bg-gbe hover:bg-gbe/90 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Budget &amp; Stock Input
            </Button>
          )}
        </div>
      }
    >
      {busy && <div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" />}
      {!busy && data && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            <KPI label="Sales" value={fmtCurrency(k.sales)} icon={DollarSign} />
            <KPI label="Budget Sales" value={fmtCurrency(k.budget_sales)} icon={Target}
                 sub={`${k.sales_to_budget_pct}% of budget`}
                 subColour={k.sales_to_budget_pct >= 100 ? "text-emerald-600" : "text-rose-600"} />
            <KPI label="COGS" value={fmtCurrency(k.cogs)} icon={PieChart} />
            <KPI label="Gross Profit" value={fmtCurrency(k.gross_profit)} icon={TrendingUp} />
            <KPI label="Gross Margin %" value={`${k.gross_margin_pct}%`}
                 icon={gmDelta != null && gmDelta >= 0 ? TrendingUp : TrendingDown}
                 sub={gmDelta != null ? `${gmDelta > 0 ? "+" : ""}${gmDelta.toFixed(2)}pp vs LY` : "—"}
                 subColour={gmDelta != null && gmDelta >= 0 ? "text-emerald-600" : "text-rose-600"} />
            <KPI label="Data Rows" value={k.row_count} icon={PieChart} />
          </div>

          {/* Cutover admin — MYOB → Xero transition */}
          {cutover && can("finance", "edit") && (
            <div data-testid="finance-cutover-card" className="rounded-lg border border-slate-200 bg-white p-4 mb-5">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Data source cutover</div>
                  <div className="text-sm text-slate-700 mt-0.5">
                    Months <span className="font-mono">&lt; {cutover.cutover_month}</span> → <SourcePill source="myob" /> ·
                    Months <span className="font-mono">&ge; {cutover.cutover_month}</span> → <SourcePill source="xero" />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Current rows: {Object.entries(cutover.source_distribution || {}).map(([k, v]) => (
                      <span key={k} className="mr-2"><SourcePill source={k} /> {v}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block">Cutover date</label>
                  <Input
                    data-testid="finance-cutover-input"
                    type="date"
                    value={cutover.cutover_date || ""}
                    onChange={(e) => setCutover({ ...cutover, cutover_date: e.target.value })}
                    className="h-9 w-[170px]"
                  />
                </div>
                <div className="flex gap-2 mt-4 md:mt-0">
                  <Button
                    data-testid="finance-cutover-save"
                    size="sm"
                    variant="outline"
                    disabled={cutoverSaving}
                    onClick={async () => {
                      setCutoverSaving(true);
                      try {
                        await api.put("/finance/cutover", { cutover_date: cutover.cutover_date });
                        const r = await api.get("/finance/cutover");
                        setCutover(r.data);
                        toast.success("Cutover updated");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to save");
                      } finally { setCutoverSaving(false); }
                    }}
                  >
                    Save date
                  </Button>
                  <Button
                    data-testid="finance-cutover-rebuild"
                    size="sm"
                    disabled={cutoverSaving}
                    className="bg-gbe hover:bg-gbe-hover text-white"
                    onClick={async () => {
                      if (!window.confirm("Rebuild finance_periods from MYOB + Xero raw data? Existing seeded and manual rows will be retained as fallback.")) return;
                      setCutoverSaving(true);
                      try {
                        const { data } = await api.post("/finance/rebuild");
                        toast.success(`Rebuilt · MYOB ${data.myob_upserts} · Xero ${data.xero_upserts} · Seed tagged ${data.legacy_seed_tagged}`);
                        const r = await api.get("/finance/cutover");
                        setCutover(r.data);
                        setReloadTick((t) => t + 1);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Rebuild failed");
                      } finally { setCutoverSaving(false); }
                    }}
                  >
                    {cutoverSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rebuild from sources"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Proactive AI insights */}
          <div className="rounded-lg border border-slate-200 bg-gradient-to-r from-gbe/95 to-gbe text-white p-4 mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="w-4 h-4 text-gbe-accent" />
                Executive briefing
                <span className="text-[10px] uppercase tracking-wider text-white/60">generated by AI · cached daily</span>
              </div>
              {insightsBusy && <Loader2 className="w-4 h-4 animate-spin text-white/70" />}
            </div>
            {insightsBusy && (
              <div className="text-xs text-white/70">Analysing the numbers…</div>
            )}
            {!insightsBusy && insights && insights.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
                {insights.slice(0, 3).map((ins, i) => {
                  const tag = (ins.tag || "").toUpperCase();
                  const tagCls = tag === "GOOD" ? "bg-emerald-500/25 text-emerald-100"
                    : tag === "ACTION" ? "bg-rose-500/25 text-rose-100"
                    : "bg-amber-500/25 text-amber-100";
                  return (
                    <div key={`insight-${ins.headline || i}`} data-testid={`finance-insight-${i}`} className="rounded-md bg-white/5 border border-white/10 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-sm text-white">{ins.headline}</div>
                        {tag && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tagCls}`}>{tag}</span>}
                      </div>
                      <div className="text-xs text-white/80 mt-1.5 leading-relaxed">{ins.text?.replace(/\s*\[[A-Z]+\]\s*$/,"")}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {!insightsBusy && (!insights || insights.length === 0) && (
              <div className="text-xs text-white/70">No insights available right now.</div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            {/* GM% trend */}
            <div className="col-span-2 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-900">Gross Margin % Over Time</div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full" style={{ background: GBE_NAVY }} /> Current</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full" style={{ background: GBE_YELLOW, boxShadow: "0 0 0 1px #cbd5e1" }} /> Last Year</span>
                </div>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                    <XAxis dataKey="month_label" stroke="#64748b" fontSize={11} />
                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Line type="monotone" dataKey="gm_pct" stroke={GBE_NAVY} strokeWidth={2.5} dot={{ r: 3 }} name="GM% current" />
                    <Line type="monotone" dataKey="gm_pct_ly" stroke={GBE_YELLOW} strokeWidth={2.5} dot={{ r: 3 }} strokeDasharray="4 3" name="GM% LY" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sales-to-budget by BU */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900 mb-2">Sales to Budget · by BU</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.sales_to_budget_by_bu} layout="vertical" margin={{ top: 5, right: 10, left: 20, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#64748b" fontSize={11} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="business_unit_name" stroke="#64748b" fontSize={10} width={100} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="sales_to_budget_pct">
                      {data.sales_to_budget_by_bu.map((row) => (
                        <Cell key={row.business_unit_name} fill={row.sales_to_budget_pct >= 100 ? GBE_NAVY : GBE_YELLOW} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Detail table */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.7fr 0.6fr" }}>
              <div>BU / Period</div>
              <div className="text-right">Budget</div>
              <div className="text-right">Sales</div>
              <div className="text-right">Materials</div>
              <div className="text-right">Wages</div>
              <div className="text-right">COGS</div>
              <div className="text-right">GP</div>
              <div className="text-right">GM %</div>
              <div className="text-right">GM % LY</div>
              <div className="text-right">Source</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {data.rows.length === 0 && (
                <div className="p-10 text-center text-sm text-slate-500">No rows for the selected filter.</div>
              )}
              {data.rows.slice(0, 200).map((r) => (
                <div key={`${r.business_unit_id}-${r.period_month}`} className="grid gap-2 px-4 py-2 text-sm text-slate-700 tabular-nums" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr 1fr 1fr 0.7fr 0.7fr 0.6fr" }}>
                  <div className="truncate">
                    <div className="text-slate-900">{r.business_unit_name}</div>
                    <div className="text-xs text-slate-500">{MONTH_LABEL(r.period_month)}</div>
                  </div>
                  <div className="text-right">{fmtCurrency(r.budget_sales)}</div>
                  <div className="text-right">{fmtCurrency(r.sales)}</div>
                  <div className="text-right">{fmtCurrency(r.materials)}</div>
                  <div className="text-right">{fmtCurrency(r.direct_wages)}</div>
                  <div className="text-right">{fmtCurrency(r.cogs)}</div>
                  <div className="text-right">{fmtCurrency(r.gross_profit)}</div>
                  <div className={`text-right font-medium ${r.gross_margin_pct >= (r.ly_gm_pct || 0) ? "text-emerald-700" : "text-rose-700"}`}>{r.gross_margin_pct}%</div>
                  <div className="text-right text-slate-500">{r.ly_gm_pct != null ? `${r.ly_gm_pct}%` : "—"}</div>
                  <div className="text-right">
                    <SourcePill source={r.source_system} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Budget & Stock Input dialog */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Budget &amp; Stock Input</DialogTitle>
            <DialogDescription>Manually enter or update a single BU × month P&L row. Upserts by (BU + period).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Business Unit</label>
              <Select value={entryForm.business_unit_id} onValueChange={(v) => setEntryForm({ ...entryForm, business_unit_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {bus.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Period (YYYY-MM)</label>
              <Input value={entryForm.period_month || ""} onChange={(e) => setEntryForm({ ...entryForm, period_month: e.target.value })} placeholder="2026-02" className="mt-1" />
            </div>
            {[
              ["budget_sales", "Budget Sales"],
              ["sales", "Sales"],
              ["opening_stock", "Opening Stock"],
              ["materials", "Materials"],
              ["direct_wages", "Direct Wages"],
              ["subcontractors", "Subcontractors"],
              ["labour_hire", "Labour Hire"],
              ["closing_stock", "Closing Stock"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-medium text-slate-700">{label}</label>
                <Input type="number" value={entryForm[key] ?? ""} onChange={(e) => setEntryForm({ ...entryForm, [key]: e.target.value })} className="mt-1" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryOpen(false)}>Cancel</Button>
            <Button data-testid={TID.financeBudgetSave} onClick={saveEntry} disabled={entrySaving} className="bg-gbe hover:bg-gbe/90 text-white">
              {entrySaving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Save entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
